import { computeSnoozeTargets, DEADLINE_URGENCY_DEFAULT } from "@app/core";
import { Button } from "@app/ui";
import { MatrixCompare } from "../../features/demos/MatrixCompare.js";
import { DemoDisclosure } from "../../features/demos/DemoDisclosure.js";
import { ReminderField } from "../../features/task-form/ReminderField.js";
import type { I18nKey } from "../../lib/language.js";
import { useAppLocale } from "../../lib/useAppLocale.js";
import { useI18n } from "../../lib/useI18n.js";
import { useTimeFormat } from "../../lib/useTimeFormat.js";
import { useCalendarsStore, useSettingsStore, useTodayLayoutStore } from "../../stores/index.js";
import {
  AGENDA_DAY_CHOICES, AGENDA_DAYS_DEFAULT,
  DEADLINE_URGENCY_DAY_MAX, DEADLINE_URGENCY_DAY_MIN,
  DEFAULT_DURATION_DEFAULT, DEFAULT_DURATION_MAX, DEFAULT_DURATION_MIN,
  type DesktopFormVariant, type MatrixScope, type SubtaskRollup,
} from "../../stores/settings.store.js";
import { SettingsCard } from "./SettingsCard.js";
import { SettingsSection } from "./SettingsSection.js";

const DURATIONS = [15, 30, 45, 60, 90, 120, 240];

const MATRIX_SCOPES: { value: MatrixScope; labelKey: I18nKey }[] = [
  { value: "off", labelKey: "taskSettings.matrixScope.off" },
  { value: "tasks", labelKey: "taskSettings.matrixScope.tasks" },
  { value: "tasks_events", labelKey: "taskSettings.matrixScope.tasksEvents" },
];

const FORM_VARIANTS: { value: DesktopFormVariant; labelKey: I18nKey }[] = [
  { value: "paper", labelKey: "taskSettings.desktopFormVariant.paper" },
  { value: "modern", labelKey: "taskSettings.desktopFormVariant.modern" },
];

const ROLLUPS: { value: SubtaskRollup; labelKey: I18nKey }[] = [
  { value: "two_way", labelKey: "taskSettings.rollup.twoWay" },
  { value: "children_to_parent", labelKey: "taskSettings.rollup.childrenToParent" },
  { value: "parent_to_children", labelKey: "taskSettings.rollup.parentToChildren" },
  { value: "ask", labelKey: "taskSettings.rollup.ask" },
];

/** "Tasks & calendar" — everything about the items you create: the defaults new
 *  entries start with, plus how tasks, the editor and the calendar behave. */
export function TasksSettings() {
  const { t } = useI18n();
  const calendars = useCalendarsStore((s) => s.calendars);
  const defaultCalendarId = useSettingsStore((s) => s.defaultCalendarId);
  const setDefaultCalendarId = useSettingsStore((s) => s.setDefaultCalendarId);
  const defaultEntryIsEvent = useSettingsStore((s) => s.defaultEntryIsEvent);
  const setDefaultEntryIsEvent = useSettingsStore((s) => s.setDefaultEntryIsEvent);
  const defaultDurationMin = useSettingsStore((s) => s.defaultDurationMin);
  const setDefaultDurationMin = useSettingsStore((s) => s.setDefaultDurationMin);
  const defaultReminderMinutes = useSettingsStore((s) => s.defaultReminderMinutes);
  const setDefaultReminderMinutes = useSettingsStore((s) => s.setDefaultReminderMinutes);
  const defaultEventReminderMinutes = useSettingsStore((s) => s.defaultEventReminderMinutes);
  const setDefaultEventReminderMinutes = useSettingsStore((s) => s.setDefaultEventReminderMinutes);
  const snoozeMorningHour = useSettingsStore((s) => s.snoozeMorningHour);
  const snoozeEveningHour = useSettingsStore((s) => s.snoozeEveningHour);
  const snoozeAfternoonHour = useSettingsStore((s) => s.snoozeAfternoonHour);
  const setSnoozeHours = useSettingsStore((s) => s.setSnoozeHours);
  const subtaskRollup = useSettingsStore((s) => s.subtaskRollup);
  const setSubtaskRollup = useSettingsStore((s) => s.setSubtaskRollup);
  const matrixScope = useSettingsStore((s) => s.matrixScope);
  const setMatrixScope = useSettingsStore((s) => s.setMatrixScope);
  const deadlineUrgency = useSettingsStore((s) => s.deadlineUrgency);
  const setDeadlineUrgency = useSettingsStore((s) => s.setDeadlineUrgency);
  const deadlineUrgencyHighDays = useSettingsStore((s) => s.deadlineUrgencyHighDays);
  const setDeadlineUrgencyHighDays = useSettingsStore((s) => s.setDeadlineUrgencyHighDays);
  const deadlineUrgencyMediumDays = useSettingsStore((s) => s.deadlineUrgencyMediumDays);
  const setDeadlineUrgencyMediumDays = useSettingsStore((s) => s.setDeadlineUrgencyMediumDays);
  const desktopFormVariant = useSettingsStore((s) => s.desktopFormVariant);
  const setDesktopFormVariant = useSettingsStore((s) => s.setDesktopFormVariant);
  const mobileTopicDisplay = useSettingsStore((s) => s.mobileTopicDisplay);
  const setMobileTopicDisplay = useSettingsStore((s) => s.setMobileTopicDisplay);
  const mobileTopicMulti = useSettingsStore((s) => s.mobileTopicMulti);
  const setMobileTopicMulti = useSettingsStore((s) => s.setMobileTopicMulti);
  const showCalendarSwipeHint = useSettingsStore((s) => s.showCalendarSwipeHint);
  const setShowCalendarSwipeHint = useSettingsStore((s) => s.setShowCalendarSwipeHint);
  const agendaDays = useSettingsStore((s) => s.agendaDays);
  const setAgendaDays = useSettingsStore((s) => s.setAgendaDays);
  const agendaShowEmptyDays = useSettingsStore((s) => s.agendaShowEmptyDays);
  const setAgendaShowEmptyDays = useSettingsStore((s) => s.setAgendaShowEmptyDays);
  const resetTodayLayout = useTodayLayoutStore((s) => s.reset);
  const tf = useTimeFormat();
  const locale = useAppLocale();

  const targets = computeSnoozeTargets(new Date(), { morningHour: snoozeMorningHour, eveningHour: snoozeEveningHour });
  const previewOf = (id: string) => {
    const at = targets.find((x) => x.id === id)?.atIso;
    if (!at) return "";
    const d = new Date(at);
    return `${d.toLocaleDateString(locale, { weekday: "short" })} ${tf.hm(d.getHours(), d.getMinutes())}`;
  };

  return (
    <div className="flex flex-col gap-8">
      <SettingsSection title={t("defaults.newEntries")} description={t("defaults.newEntries.description")}>
        <SettingsCard id="default-calendar" title={t("defaults.defaultCalendar")} description={t("defaults.defaultCalendar.description")}>
          <select
            value={defaultCalendarId ?? ""}
            onChange={(e) => void setDefaultCalendarId(e.target.value || null)}
            className="rounded-md border border-input bg-background px-2 py-1.5 text-sm"
          >
            {calendars.length === 0 && <option value="">{t("defaults.noCalendars")}</option>}
            {calendars.map((c) => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>
        </SettingsCard>

        <SettingsCard id="default-type" title={t("defaults.defaultType")} description={t("defaults.defaultType.description")} onReset={() => void setDefaultEntryIsEvent(true)}>
          <div className="flex gap-2">
            <Button variant={defaultEntryIsEvent ? "primary" : "outline"} onClick={() => void setDefaultEntryIsEvent(true)}>{t("common.event")}</Button>
            <Button variant={!defaultEntryIsEvent ? "primary" : "outline"} onClick={() => void setDefaultEntryIsEvent(false)}>{t("common.task")}</Button>
          </div>
        </SettingsCard>

        <SettingsCard id="default-duration" title={t("defaults.defaultDuration")} description={t("defaults.defaultDuration.description")} onReset={() => void setDefaultDurationMin(DEFAULT_DURATION_DEFAULT)}>
          <div className="flex flex-wrap items-center gap-2">
            {DURATIONS.map((m) => (
              <Button key={m} size="sm" variant={defaultDurationMin === m ? "primary" : "outline"} onClick={() => void setDefaultDurationMin(m)}>
                {m % 60 === 0 ? `${m / 60}h` : `${m}m`}
              </Button>
            ))}
            <input
              type="number"
              min={DEFAULT_DURATION_MIN}
              max={DEFAULT_DURATION_MAX}
              step={5}
              value={defaultDurationMin}
              onChange={(e) => void setDefaultDurationMin(Number(e.target.value))}
              className="w-20 rounded-md border border-input bg-background px-2 py-1 text-sm"
              aria-label={t("defaults.customDurationAria")}
            />
            <span className="text-sm text-muted-foreground">min</span>
          </div>
        </SettingsCard>
      </SettingsSection>

      <SettingsSection title={t("taskSettings.remindersSnooze")} description={t("defaults.reminders.description")}>
        <SettingsCard id="default-task-reminders" title={t("defaults.taskReminders")} onReset={() => void setDefaultReminderMinutes([0])}>
          <ReminderField value={defaultReminderMinutes} onChange={(v) => void setDefaultReminderMinutes(v)} />
        </SettingsCard>

        <SettingsCard id="default-event-reminders" title={t("defaults.eventReminders")} description={t("defaults.eventReminders.description")} onReset={() => void setDefaultEventReminderMinutes([])}>
          <ReminderField value={defaultEventReminderMinutes} onChange={(v) => void setDefaultEventReminderMinutes(v)} />
        </SettingsCard>

        <SettingsCard
          id="snooze"
          title={t("defaults.snoozeAnchors")}
          description={t("defaults.snoozeAnchors.description")}
        >
          <div className="flex flex-wrap items-end gap-6">
            <label className="flex flex-col gap-1 text-sm">
              <span className="text-muted-foreground">{t("defaults.morning")}</span>
              <HourSelect value={snoozeMorningHour} onChange={(h) => void setSnoozeHours(h, snoozeEveningHour)} tf={tf} />
            </label>
            <label className="flex flex-col gap-1 text-sm">
              <span className="text-muted-foreground">{t("defaults.afternoon")}</span>
              <HourSelect value={snoozeAfternoonHour} onChange={(h) => void setSnoozeHours(snoozeMorningHour, snoozeEveningHour, h)} tf={tf} />
            </label>
            <label className="flex flex-col gap-1 text-sm">
              <span className="text-muted-foreground">{t("defaults.evening")}</span>
              <HourSelect value={snoozeEveningHour} onChange={(h) => void setSnoozeHours(snoozeMorningHour, h)} tf={tf} />
            </label>
          </div>
          <p className="mt-3 text-xs text-muted-foreground">
            {t("defaults.snoozePreview", { tomorrow: previewOf("tomorrow"), evening: previewOf("evening") })}
          </p>
        </SettingsCard>
      </SettingsSection>

      <SettingsSection title={t("taskSettings.todayView")}>
        <SettingsCard
          id="agenda-days"
          title={t("taskSettings.agendaDays")}
          description={t("taskSettings.agendaDays.description")}
          onReset={() => {
            void setAgendaDays(AGENDA_DAYS_DEFAULT);
            void setAgendaShowEmptyDays(false);
            resetTodayLayout();
          }}
          demoId="today-tour"
        >
          <div className="flex flex-wrap items-center gap-2">
            {AGENDA_DAY_CHOICES.map((d) => (
              <Button
                key={d}
                size="sm"
                variant={agendaDays === d ? "primary" : "outline"}
                aria-pressed={agendaDays === d}
                onClick={() => void setAgendaDays(d)}
              >
                {t("taskSettings.agendaDays.option", { count: d })}
              </Button>
            ))}
          </div>
          <div className="mt-4 flex flex-col gap-2">
            <span className="text-sm text-muted-foreground">{t("taskSettings.agendaShowEmpty")}</span>
            <div className="flex gap-2">
              <Button aria-pressed={agendaShowEmptyDays} variant={agendaShowEmptyDays ? "primary" : "outline"} onClick={() => void setAgendaShowEmptyDays(true)}>{t("common.show")}</Button>
              <Button aria-pressed={!agendaShowEmptyDays} variant={!agendaShowEmptyDays ? "primary" : "outline"} onClick={() => void setAgendaShowEmptyDays(false)}>{t("common.hide")}</Button>
            </div>
          </div>
        </SettingsCard>
      </SettingsSection>

      <SettingsSection title={t("taskSettings.subtasks")}>
        <SettingsCard id="subtask-rollup" title={t("taskSettings.subtaskCompletion")} description={t("taskSettings.subtaskCompletion.description")} onReset={() => void setSubtaskRollup("two_way")} demoId="setting-subtask-rollup">
          <div className="flex flex-wrap gap-2">
            {ROLLUPS.map((r) => (
              <Button key={r.value} variant={subtaskRollup === r.value ? "primary" : "outline"} onClick={() => void setSubtaskRollup(r.value)}>
                {t(r.labelKey)}
              </Button>
            ))}
          </div>
        </SettingsCard>
      </SettingsSection>

      <SettingsSection title={t("taskSettings.editor")}>
        <SettingsCard
          id="desktop-form-variant"
          title={t("taskSettings.desktopFormVariant")}
          description={t("taskSettings.desktopFormVariant.description")}
          onReset={() => void setDesktopFormVariant("paper")}
        >
          <div className="flex flex-wrap gap-2">
            {FORM_VARIANTS.map((v) => (
              <Button key={v.value} variant={desktopFormVariant === v.value ? "primary" : "outline"} onClick={() => void setDesktopFormVariant(v.value)}>
                {t(v.labelKey)}
              </Button>
            ))}
          </div>
        </SettingsCard>

        <SettingsCard
          id="matrix-scope"
          title={t("taskSettings.matrixScope")}
          description={t("taskSettings.matrixScope.description")}
          onReset={() => void setMatrixScope("tasks_events")}
        >
          <div className="flex flex-wrap gap-2">
            {MATRIX_SCOPES.map((s) => (
              <Button key={s.value} variant={matrixScope === s.value ? "primary" : "outline"} onClick={() => void setMatrixScope(s.value)}>
                {t(s.labelKey)}
              </Button>
            ))}
          </div>
          <MatrixCompare scope={matrixScope} />
          <DemoDisclosure id="setting-matrix-scope" />
          {matrixScope !== "off" && (
            <SettingsCard
              id="deadline-urgency"
              title={t("taskSettings.deadlineUrgency")}
              description={t("taskSettings.deadlineUrgency.description")}
              demoId="setting-deadline-urgency"
              className="mt-4 border-dashed bg-muted/20 shadow-none"
              onReset={() => {
                void setDeadlineUrgency(DEADLINE_URGENCY_DEFAULT.enabled);
                void setDeadlineUrgencyHighDays(DEADLINE_URGENCY_DEFAULT.highWithinDays);
                void setDeadlineUrgencyMediumDays(DEADLINE_URGENCY_DEFAULT.mediumWithinDays);
              }}
            >
              <div className="flex flex-wrap gap-2">
                <Button variant={deadlineUrgency ? "primary" : "outline"} onClick={() => void setDeadlineUrgency(true)}>
                  {t("taskSettings.deadlineUrgency.on")}
                </Button>
                <Button variant={!deadlineUrgency ? "primary" : "outline"} onClick={() => void setDeadlineUrgency(false)}>
                  {t("taskSettings.deadlineUrgency.off")}
                </Button>
              </div>
              {deadlineUrgency && (
                <>
                  <div className="mt-4 flex flex-wrap items-end gap-6">
                    <label className="flex flex-col gap-1 text-sm">
                      <span className="text-muted-foreground">{t("taskSettings.deadlineUrgency.highDays")}</span>
                      <input
                        type="number"
                        min={DEADLINE_URGENCY_DAY_MIN}
                        max={DEADLINE_URGENCY_DAY_MAX}
                        value={deadlineUrgencyHighDays}
                        onChange={(e) => void setDeadlineUrgencyHighDays(Number(e.target.value))}
                        className="w-20 rounded-md border border-input bg-background px-2 py-1 text-sm"
                      />
                    </label>
                    <label className="flex flex-col gap-1 text-sm">
                      <span className="text-muted-foreground">{t("taskSettings.deadlineUrgency.mediumDays")}</span>
                      <input
                        type="number"
                        min={DEADLINE_URGENCY_DAY_MIN}
                        max={DEADLINE_URGENCY_DAY_MAX}
                        value={deadlineUrgencyMediumDays}
                        onChange={(e) => void setDeadlineUrgencyMediumDays(Number(e.target.value))}
                        className="w-20 rounded-md border border-input bg-background px-2 py-1 text-sm"
                      />
                    </label>
                  </div>
                  <p className="mt-3 text-xs text-muted-foreground">
                    {t("taskSettings.deadlineUrgency.thresholdHint", { high: String(deadlineUrgencyHighDays), medium: String(deadlineUrgencyMediumDays) })}
                  </p>
                </>
              )}
            </SettingsCard>
          )}
        </SettingsCard>

        <SettingsCard
          id="topic-editor"
          title={t("taskSettings.topicEditor")}
          description={t("taskSettings.topicEditor.description")}
        >
          <div className="mb-4">
            <span className="mb-1 block text-sm text-muted-foreground">{t("taskSettings.topicButtons")}</span>
            <div className="flex flex-wrap gap-2">
              <Button variant={mobileTopicDisplay === "icons+text" ? "primary" : "outline"} onClick={() => void setMobileTopicDisplay("icons+text")}>{t("taskSettings.iconsText")}</Button>
              <Button variant={mobileTopicDisplay === "icons" ? "primary" : "outline"} onClick={() => void setMobileTopicDisplay("icons")}>{t("taskSettings.icons")}</Button>
              <Button variant={mobileTopicDisplay === "text" ? "primary" : "outline"} onClick={() => void setMobileTopicDisplay("text")}>{t("taskSettings.text")}</Button>
            </div>
          </div>
          <div>
            <span className="mb-1 block text-sm text-muted-foreground">{t("taskSettings.topicsPerTask")}</span>
            <div className="flex flex-wrap gap-2">
              <Button variant={mobileTopicMulti ? "primary" : "outline"} onClick={() => void setMobileTopicMulti(true)}>{t("taskSettings.multiple")}</Button>
              <Button variant={!mobileTopicMulti ? "primary" : "outline"} onClick={() => void setMobileTopicMulti(false)}>{t("taskSettings.single")}</Button>
            </div>
          </div>
        </SettingsCard>
      </SettingsSection>

      <SettingsSection title={t("taskSettings.mobile")}>
        <SettingsCard
          id="swipe-hint"
          title={t("taskSettings.swipeHint")}
          description={t("taskSettings.swipeHint.description")}
        >
          <div className="flex gap-2">
            <Button variant={showCalendarSwipeHint ? "primary" : "outline"} onClick={() => void setShowCalendarSwipeHint(true)}>{t("common.show")}</Button>
            <Button variant={!showCalendarSwipeHint ? "primary" : "outline"} onClick={() => void setShowCalendarSwipeHint(false)}>{t("common.hide")}</Button>
          </div>
        </SettingsCard>
      </SettingsSection>
    </div>
  );
}

function HourSelect({ value, onChange, tf }: { value: number; onChange: (h: number) => void; tf: ReturnType<typeof useTimeFormat> }) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(Number(e.target.value))}
      className="rounded-md border border-input bg-background px-2 py-1.5 text-sm"
    >
      {Array.from({ length: 24 }, (_, h) => (
        <option key={h} value={h}>{tf.hm(h, 0)}</option>
      ))}
    </select>
  );
}
